import BaseQuestionModel from '../BaseQuestionModel'
import { IQuestionModelConstructor, QuestionType } from '../types'

import { IMenuQuestionModelState, IMenuResponseValidation, MenuQuestionModelTypes, MenuResponseAnswer } from './types'

const MenuQuestionModel: IQuestionModelConstructor<MenuQuestionModelTypes> = (questionSchema, action) => {
  const { model } = BaseQuestionModel<MenuQuestionModelTypes, IMenuQuestionModelState>({
    questionType: QuestionType.MENU,
    questionSchema: questionSchema,
    action,
    submitResponse,
    initState,
    validateResponse
  })
  return model
}

function submitResponse({ response, questionSchema }: IMenuQuestionModelState) {
  const naAnswer = questionSchema.answers.find(a => a.na)
  const naChecked = response.na
  const resp = []

  if (naChecked && naAnswer) {
    // if NA is checked, only send the NA answer
    resp.push({ id: naAnswer.id, v: 1 })
  } else {
    // aggregate quantities for each menu item
    const productTotals: Record<string, number> = {}
    for (const section of response.data) {
      for (const answer of section.answers) {
        if (answer.v > 0) {
          productTotals[answer.id] = (productTotals[answer.id] || 0) + answer.v
        }
      }
    }
    for (const [productId, totalQuantity] of Object.entries(productTotals)) {
      resp.push({ id: productId, v: totalQuantity })
    }

    // comment if present
    if (response.c && response.c.trim().length > 0) {
      resp.push({ id: questionSchema.id, c: response.c.trim() })
    }
  }
  return resp
}

function initState(state: IMenuQuestionModelState): IMenuQuestionModelState {
  const schema = state.questionSchema
  const matrix_batches = schema?.matrix_batches || []
  const answersBySection: { sectionId: string; answers: MenuResponseAnswer[] }[] = []

  if (matrix_batches.length > 0) {
    for (const section of matrix_batches) {
      const productIds = section.products ?? []
      const sectionAnswers: MenuResponseAnswer[] = productIds
        .map(productId => {
          const answer = schema.answers?.find(ans => ans.id === productId)
          if (!answer) return null
          const answerResp: MenuResponseAnswer = { id: answer.id, v: 0, price: answer.price || 0 }
          if (answer.na) {
            answerResp.na = true
          }
          return answerResp
        })
        .filter((a): a is MenuResponseAnswer => a !== null)
      answersBySection.push({ sectionId: section.id, answers: sectionAnswers })
    }
    state.responseValidation = { valid: false }
  } else {
    state.responseValidation = { valid: true }
  }

  state.response = {
    orderPlaced: false,
    data: answersBySection
  }
  if (schema.na) state.response.na = false
  if (schema.delivery_options) state.response.deliveryOption = undefined

  return state
}

function validateResponse({ response, questionSchema }: IMenuQuestionModelState): IMenuResponseValidation {
  const hasSections = (questionSchema.matrix_batches?.length ?? 0) > 0
  const hasProducts = hasSections && questionSchema.matrix_batches.some(section => (section.products?.length ?? 0) > 0)

  const needsDelivery = Boolean(questionSchema.delivery_options?.length)
  const hasDelivery = !needsDelivery || Boolean(response.deliveryOption)

  const isValid =
    !hasSections || // no sections
    !hasProducts || // sections are empty
    (questionSchema.na && response.na) ||
    (response.orderPlaced && hasDelivery)

  return { valid: isValid }
}

export default MenuQuestionModel
